import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ShoppingCart, User, LogOut, Settings, LayoutDashboard, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useCart } from '@/contexts/CartContext';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { motion } from '@/lib/motion-wrapper';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, signOut, isLoading, isAdmin } = useAuth();
  const { totalItems } = useCart();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Health Benefits', path: '/health-benefits' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
    { name: 'Sales Team', path: '/sales-team' },
    { name: 'Track Order', path: '/track-order' },
  ];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      // Force redirect to home page even if there's an error
      window.location.href = '/';
    }
  };

  const handleCartClick = () => {
    navigate('/checkout');
    // Ensure page scrolls to top after navigation
    window.scrollTo(0, 0);
  };

  return (
    <header className="bg-cream-100 border-b border-brand-200 sticky top-0 z-50">
      <nav className="container-custom py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Updated to use text instead of image */}
          <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
            <span className="text-2xl font-playfair font-semibold text-gray-800 hover:text-gray-900 transition-colors">
              Natural <span className="font-extrabold text-[#D4AF37]">Puff</span><sup className="text-xs ml-0.5">™</sup>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link 
                key={link.name} 
                to={link.path}
                className={`relative group ${
                  location.pathname === link.path ? 'font-medium text-brand-600' : 'text-brand-800 hover:text-brand-600'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-[2px] bg-brand-600 transition-transform duration-150 ease-in-out group-hover:w-full ${
                  location.pathname === link.path ? 'w-0' : ''
                }`} />
              </Link>
            ))}
          </div>

          {/* Cart and Authentication Links */}
          <div className="hidden md:flex items-center space-x-4">
            <Button 
              variant="outline" 
              size="icon"
              className="border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white relative"
              onClick={handleCartClick}
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold-500 text-xs text-white rounded-full h-5 w-5 flex items-center justify-center">{totalItems}</span>
              )}
            </Button>
            
            {isLoading ? (
              <div className="h-10 w-24 bg-gray-200 animate-pulse rounded"></div>
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white transition-colors duration-150"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Account
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 p-0 shadow-2xl border-2 border-brand-200 bg-cream-50 rounded-xl overflow-hidden">
                  {/* User Info Header */}
                  <div className="px-4 py-4 bg-gradient-to-br from-brand-600 to-brand-700 relative overflow-hidden">
                    {/* Decorative pattern overlay */}
                    <div className="absolute inset-0 opacity-10 bg-pattern"></div>
                    <div className="relative flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center shadow-lg ring-2 ring-cream-100/50">
                        <User className="h-6 w-6 text-brand-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate font-playfair">
                          {user?.email?.split('@')[0] || 'User'}
                        </p>
                        <p className="text-xs text-cream-100 truncate">
                          {user?.email || 'user@example.com'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Menu Items Container */}
                  <div className="p-2">
                    {/* My Profile */}
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className="px-3 py-3 cursor-pointer rounded-lg hover:bg-cream-100 transition-all duration-150 group focus:bg-cream-100 mb-1"
                    >
                      <div className="flex items-center space-x-3 w-full">
                        <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-150">
                          <User className="h-5 w-5 text-brand-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-brand-800">My Profile</p>
                          <p className="text-xs text-brand-600">View and edit your profile</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-brand-400 flex-shrink-0" />
                      </div>
                    </DropdownMenuItem>
                    
                    {/* Admin Dashboard */}
                    {isAdmin && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/dashboard/admin')}
                        className="px-3 py-3 cursor-pointer rounded-lg hover:bg-gold-50 transition-all duration-150 group focus:bg-gold-50 mb-1"
                      >
                        <div className="flex items-center space-x-3 w-full">
                          <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-150">
                            <LayoutDashboard className="h-5 w-5 text-gold-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-brand-800">Admin Dashboard</p>
                            <p className="text-xs text-brand-600">Manage your store</p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-brand-400 flex-shrink-0" />
                        </div>
                      </DropdownMenuItem>
                    )}
                    
                    {/* Divider */}
                    <div className="my-2 border-t border-brand-200"></div>
                    
                    {/* Sign Out */}
                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      className="px-3 py-3 cursor-pointer rounded-lg hover:bg-red-50 transition-all duration-150 group focus:bg-red-50"
                    >
                      <div className="flex items-center space-x-3 w-full">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-150">
                          <LogOut className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-red-600">Sign Out</p>
                          <p className="text-xs text-brand-600">Logout from your account</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button variant="outline" className="border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white">
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-brand-600 text-white hover:bg-brand-700">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button and Cart */}
          <div className="md:hidden flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="icon"
              className="border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white relative"
              onClick={handleCartClick}
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold-500 text-xs text-white rounded-full h-5 w-5 flex items-center justify-center">{totalItems}</span>
              )}
            </Button>
            
            <button 
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-brand-800 hover:bg-brand-100"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 absolute left-0 right-0 top-full bg-cream-100 border-b border-brand-200 shadow-lg animate-fade-in">
            <div className="flex flex-col space-y-4 px-6 pb-6">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`relative group py-2 ${
                    location.pathname === link.path ? 'font-medium text-brand-600' : 'text-brand-800 hover:text-brand-600'
                  }`}
                  onClick={closeMobileMenu}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 w-0 h-[2px] bg-brand-600 transition-transform duration-150 ease-in-out group-hover:w-full ${
                    location.pathname === link.path ? 'w-0' : ''
                  }`} />
                </Link>
              ))}
              <div className="flex flex-col space-y-3 pt-4 border-t border-brand-200">
                {user ? (
                  <>
                    <Button 
                      variant="outline" 
                      className="w-full border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white"
                      onClick={() => {
                        navigate('/profile');
                        closeMobileMenu();
                      }}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </Button>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        className="w-full border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white"
                        onClick={() => {
                          navigate('/dashboard/admin');
                          closeMobileMenu();
                        }}
                      >
                        <User className="h-4 w-4 mr-2" />
                        Admin Dashboard
                      </Button>
                    )}
                    <Button 
                      className="w-full bg-brand-600 text-white hover:bg-brand-700"
                      onClick={() => {
                        handleSignOut();
                        closeMobileMenu();
                      }}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={closeMobileMenu} className="w-full">
                      <Button variant="outline" className="w-full border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white">
                        Login
                      </Button>
                    </Link>
                    <Link to="/signup" onClick={closeMobileMenu} className="w-full">
                      <Button className="w-full bg-brand-600 text-white hover:bg-brand-700">
                        Sign Up
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
